import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Analytics.css';

const SalesForecasting = () => {
  const [forecastData, setForecastData] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);
  const [forecastPeriod, setForecastPeriod] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    projectedRevenue: 0,
    growthRate: 0,
    confidence: 0,
    trend: 'stable'
  });

  useEffect(() => {
    fetchHistoricalData();
  }, [forecastPeriod]);

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      const daysToFetch = forecastPeriod === '7days' ? 30 : forecastPeriod === '30days' ? 90 : 180;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysToFetch);

      const q = query(
        collection(db, 'orders'),
        where('status', '==', 'completed'),
        where('timestamp', '>=', startDate),
        orderBy('timestamp', 'asc')
      );

      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : new Date()
      }));

      const dailyData = processDailyData(orders);
      setHistoricalData(dailyData);
      
      const forecast = generateForecast(dailyData);
      setForecastData(forecast);
      
      calculateMetrics(dailyData, forecast);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const processDailyData = (orders) => {
    const dailyMap = {};
    
    orders.forEach(order => {
      const date = order.timestamp.toISOString().split('T')[0];
      if (!dailyMap[date]) {
        dailyMap[date] = {
          date,
          revenue: 0,
          orders: 0
        };
      }
      dailyMap[date].revenue += order.total || 0;
      dailyMap[date].orders += 1;
    });

    return Object.values(dailyMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const generateForecast = (historicalData) => {
    if (historicalData.length < 7) return [];

    const forecastDays = forecastPeriod === '7days' ? 7 : forecastPeriod === '30days' ? 30 : 90;
    const recentData = historicalData.slice(-14);
    const avgRevenue = recentData.reduce((sum, d) => sum + d.revenue, 0) / recentData.length;
    const avgOrders = recentData.reduce((sum, d) => sum + d.orders, 0) / recentData.length;
    
    const trendSlope = calculateTrend(recentData);
    
    const forecast = [];
    const lastDate = new Date(historicalData[historicalData.length - 1].date);
    
    for (let i = 1; i <= forecastDays; i++) {
      const forecastDate = new Date(lastDate);
      forecastDate.setDate(lastDate.getDate() + i);
      
      const dayOfWeek = forecastDate.getDay();
      const weekendMultiplier = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.3 : 1.0;
      const seasonalFactor = 1 + (Math.sin(i / 7) * 0.1);
      const trendFactor = 1 + (trendSlope * i);
      
      forecast.push({
        date: forecastDate.toISOString().split('T')[0],
        projectedRevenue: Math.round(avgRevenue * weekendMultiplier * seasonalFactor * trendFactor),
        projectedOrders: Math.round(avgOrders * weekendMultiplier * seasonalFactor * trendFactor),
        confidence: Math.max(0.6, 0.95 - (i * 0.005))
      });
    }
    
    return forecast;
  };

  const calculateTrend = (data) => {
    if (data.length < 2) return 0;
    
    const revenues = data.map(d => d.revenue);
    const n = revenues.length;
    const xSum = (n * (n + 1)) / 2;
    const ySum = revenues.reduce((a, b) => a + b, 0);
    const xySum = revenues.reduce((sum, y, x) => sum + (x + 1) * y, 0);
    const xxSum = (n * (n + 1) * (2 * n + 1)) / 6;
    
    const slope = (n * xySum - xSum * ySum) / (n * xxSum - xSum * xSum);
    return slope / ySum;
  };

  const calculateMetrics = (historical, forecast) => {
    if (historical.length === 0 || forecast.length === 0) return;

    const recentRevenue = historical.slice(-7).reduce((sum, d) => sum + d.revenue, 0);
    const projectedRevenue = forecast.reduce((sum, d) => sum + d.projectedRevenue, 0);
    const growthRate = ((projectedRevenue - recentRevenue) / recentRevenue) * 100;
    const avgConfidence = forecast.reduce((sum, d) => sum + d.confidence, 0) / forecast.length;
    
    setMetrics({
      projectedRevenue,
      growthRate,
      confidence: avgConfidence * 100,
      trend: growthRate > 5 ? 'growing' : growthRate < -5 ? 'declining' : 'stable'
    });
  };

  const combinedData = [...historicalData.slice(-30), ...forecastData];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Analyzing sales data...</p>
      </div>
    );
  }

  return (
    <div className="sales-forecasting-page">
      <div className="page-header">
        <div>
          <h1>📈 Sales Forecasting</h1>
          <p>AI-powered revenue prediction and trend analysis</p>
        </div>
        <select 
          value={forecastPeriod}
          onChange={(e) => setForecastPeriod(e.target.value)}
          className="forecast-period-select"
        >
          <option value="7days">Next 7 Days</option>
          <option value="30days">Next 30 Days</option>
          <option value="90days">Next 90 Days</option>
        </select>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon" style={{background: '#3498db'}}>💰</div>
          <div className="metric-content">
            <p className="metric-label">Projected Revenue</p>
            <p className="metric-value">₹{metrics.projectedRevenue.toLocaleString()}</p>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{background: metrics.growthRate >= 0 ? '#2ecc71' : '#e74c3c'}}>
            {metrics.growthRate >= 0 ? '📈' : '📉'}
          </div>
          <div className="metric-content">
            <p className="metric-label">Growth Rate</p>
            <p className="metric-value" style={{color: metrics.growthRate >= 0 ? '#2ecc71' : '#e74c3c'}}>
              {metrics.growthRate >= 0 ? '+' : ''}{metrics.growthRate.toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{background: '#f39c12'}}>🎯</div>
          <div className="metric-content">
            <p className="metric-label">Forecast Confidence</p>
            <p className="metric-value">{metrics.confidence.toFixed(0)}%</p>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{background: '#9b59b6'}}>📊</div>
          <div className="metric-content">
            <p className="metric-label">Trend</p>
            <p className="metric-value capitalize">{metrics.trend}</p>
          </div>
        </div>
      </div>

      <div className="chart-container">
        <h3>Revenue Forecast vs Historical</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={combinedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString('en-IN', {month: 'short', day: 'numeric'})} />
            <YAxis tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
            <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#3498db" strokeWidth={2} name="Actual Revenue" dot={{ r: 3 }} />
            <Line type="monotone" dataKey="projectedRevenue" stroke="#2ecc71" strokeWidth={2} strokeDasharray="5 5" name="Projected Revenue" dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="insights-section">
        <h3>📊 Key Insights</h3>
        <div className="insights-grid">
          <div className="insight-card">
            <strong>Revenue Trend:</strong>
            <p>
              {metrics.trend === 'growing' && 'Your revenue is on an upward trajectory. Expected strong growth in the coming period.'}
              {metrics.trend === 'declining' && 'Revenue is showing a declining trend. Consider promotional strategies to boost sales.'}
              {metrics.trend === 'stable' && 'Revenue is stable with consistent performance. Focus on customer retention.'}
            </p>
          </div>
          <div className="insight-card">
            <strong>Forecast Reliability:</strong>
            <p>
              {metrics.confidence >= 85 && 'High confidence - forecast is highly reliable based on strong historical patterns.'}
              {metrics.confidence >= 70 && metrics.confidence < 85 && 'Moderate confidence - consider external factors that may impact results.'}
              {metrics.confidence < 70 && 'Lower confidence - more historical data needed for accurate predictions.'}
            </p>
          </div>
          <div className="insight-card">
            <strong>Recommendations:</strong>
            <p>
              {metrics.growthRate > 10 && 'Strong growth expected. Consider increasing inventory and staffing levels.'}
              {metrics.growthRate > 0 && metrics.growthRate <= 10 && 'Steady growth anticipated. Maintain current operations with minor optimizations.'}
              {metrics.growthRate < 0 && 'Implement marketing campaigns and promotional offers to reverse the trend.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesForecasting;
